// Flagging Engine — Rules R1 through R16
import { getCategoryConfig } from '../data/categories'

export const FLAG_CODES = {
  FIXED_UNIT_BREACH: { code: 'FIXED_UNIT_BREACH', severity: 'HIGH', label: 'Fixed unit price breach' },
  PRICE_MISMATCH: { code: 'PRICE_MISMATCH', severity: 'HIGH', label: 'Price mismatch' },
  OVERCHARGE: { code: 'OVERCHARGE', severity: 'MEDIUM', label: 'Overcharge detected' },
  UNDERCHARGE: { code: 'UNDERCHARGE', severity: 'MEDIUM', label: 'Undercharge detected' },
  HALF_PORTION_DISCOUNT: { code: 'HALF_PORTION_DISCOUNT', severity: 'MEDIUM', label: 'Half-portion below expected' },
  UNKNOWN_PRODUCT: { code: 'UNKNOWN_PRODUCT', severity: 'HIGH', label: 'Unknown product code' },
  HIGH_WASTE: { code: 'HIGH_WASTE', severity: 'HIGH', label: 'Surplus > 50% of opening' },
  MODERATE_WASTE: { code: 'MODERATE_WASTE', severity: 'MEDIUM', label: 'Surplus > 30% of opening' },
  SIGNIFICANT_UNDERPERFORMANCE: { code: 'SIGNIFICANT_UNDERPERFORMANCE', severity: 'MEDIUM', label: 'Actual < 60% of projected' },
  RESTOCK_NOT_LOGGED: { code: 'RESTOCK_NOT_LOGGED', severity: 'LOW', label: 'Opening zero but sales recorded' },
  ORPHANED_SURPLUS: { code: 'ORPHANED_SURPLUS', severity: 'MEDIUM', label: 'Surplus with zero opening' },
  DUPLICATE_LINE: { code: 'DUPLICATE_LINE', severity: 'HIGH', label: 'Duplicate product entry' },
  LOW_REVENUE_DAY: { code: 'LOW_REVENUE_DAY', severity: 'MEDIUM', label: 'Daily total < R800' },
  KHW_PRICE_DRIFT: { code: 'KHW_PRICE_DRIFT', severity: 'MEDIUM', label: 'KHW price outside expected range' },
  SYSTEMATIC_UNDERCHARGE: { code: 'SYSTEMATIC_UNDERCHARGE', severity: 'HIGH', label: 'Consecutive negative diff on same product' },
  STEAK_OVERSTOCK_PATTERN: { code: 'STEAK_OVERSTOCK_PATTERN', severity: 'MEDIUM', label: 'Steak surplus > 40% on 2+ days/week' },
  MISSING_MORNING_LOG: { code: 'MISSING_MORNING_LOG', severity: 'MEDIUM', label: 'Morning log not entered for category' },
  TILL_VARIANCE: { code: 'TILL_VARIANCE', severity: 'HIGH', label: 'Till variance detected' },
  MISSING_TILL_ENTRY: { code: 'MISSING_TILL_ENTRY', severity: 'MEDIUM', label: 'No till record for today' },
}

const HALF_PORTION_CODES = ['HST', 'HSTV', 'HMK', 'HLV', 'HML']

// R1: Mutuku/Makhaha not clean R10 multiple
export function checkFixedUnitBreach(salesRands, category) {
  if (category !== 'mutuku' && category !== 'makhaha') return null
  if (salesRands % 10 !== 0) {
    return {
      flagCode: 'FIXED_UNIT_BREACH',
      severity: 'HIGH',
      description: `${category} sales R${salesRands} is not a clean multiple of R10`,
    }
  }
  return null
}

// R2-R4: Discrete price checks
export function checkPriceMismatch(qtySold, salesRands, unitPrice, productCode) {
  if (!unitPrice || !qtySold) return null
  const expected = unitPrice * qtySold
  const diff = salesRands - expected

  if (diff === 0) return null

  // R5: Half-portion discount
  if (HALF_PORTION_CODES.includes(productCode) && diff < 0) {
    return {
      flagCode: 'HALF_PORTION_DISCOUNT',
      severity: 'MEDIUM',
      description: `${productCode}: expected R${expected}, got R${salesRands} (discount R${Math.abs(diff)})`,
    }
  }

  if (diff > 0) {
    return {
      flagCode: 'OVERCHARGE',
      severity: 'MEDIUM',
      description: `${productCode}: expected R${expected}, got R${salesRands} (+R${diff})`,
    }
  }

  return {
    flagCode: 'UNDERCHARGE',
    severity: 'MEDIUM',
    description: `${productCode}: expected R${expected}, got R${salesRands} (R${diff})`,
  }
}

// R7-R8: Waste flags
export function checkWaste(opening, surplus, category) {
  if (!opening || opening === 0 || !surplus) return null
  const pct = (surplus / opening) * 100

  if (pct > 50) {
    return {
      flagCode: 'HIGH_WASTE',
      severity: 'HIGH',
      description: `${category}: surplus ${pct.toFixed(0)}% of opening (>${'50%'})`,
    }
  }
  if (pct > 30) {
    return {
      flagCode: 'MODERATE_WASTE',
      severity: 'MEDIUM',
      description: `${category}: surplus ${pct.toFixed(0)}% of opening (>${'30%'})`,
    }
  }
  return null
}

// R9: Underperformance
export function checkUnderperformance(projected, actual, category) {
  const cat = getCategoryConfig(category)
  if (!cat?.projectable || !projected || projected === 0) return null
  if (actual < projected * 0.6) {
    return {
      flagCode: 'SIGNIFICANT_UNDERPERFORMANCE',
      severity: 'MEDIUM',
      description: `${category}: actual R${actual} < 60% of projected R${projected}`,
    }
  }
  return null
}

// R10: Restock not logged
export function checkRestockNotLogged(opening, actualSales, category) {
  if (opening === 0 && actualSales > 0) {
    return {
      flagCode: 'RESTOCK_NOT_LOGGED',
      severity: 'LOW',
      description: `${category}: zero opening but R${actualSales} in sales`,
    }
  }
  return null
}

// R11: Orphaned surplus
export function checkOrphanedSurplus(opening, surplus, category) {
  if ((!opening || opening === 0) && surplus > 0) {
    return {
      flagCode: 'ORPHANED_SURPLUS',
      severity: 'MEDIUM',
      description: `${category}: surplus of ${surplus} with zero opening`,
    }
  }
  return null
}

// R13: Low revenue day
export function checkLowRevenue(totalDailySales) {
  if (totalDailySales < 800) {
    return {
      flagCode: 'LOW_REVENUE_DAY',
      severity: 'MEDIUM',
      description: `Daily total R${totalDailySales.toFixed(0)} is below R800`,
    }
  }
  return null
}

// R14: KHW price drift
export function checkKhwPriceDrift(salesRands, qtySold, productCode) {
  if (productCode !== 'KHW' || !qtySold || qtySold === 0) return null
  const pricePerPiece = salesRands / qtySold
  if (pricePerPiece > 13 || pricePerPiece < 11) {
    return {
      flagCode: 'KHW_PRICE_DRIFT',
      severity: 'MEDIUM',
      description: `KHW: realised price R${pricePerPiece.toFixed(2)}/piece (expected R11-13)`,
    }
  }
  return null
}

// Generate all transaction-level flags for a sale
export function generateTransactionFlags(tx) {
  const flags = []

  // R1
  const f1 = checkFixedUnitBreach(tx.salesRands, tx.category)
  if (f1) flags.push({ ...f1, date: tx.date, transactionId: tx.id })

  // R2-R5
  if (tx.saleType === 'discrete' || tx.saleType === 'continuous_fixed') {
    const f2 = checkPriceMismatch(tx.qtySold, tx.salesRands, tx.unitPrice, tx.productCode)
    if (f2) flags.push({ ...f2, date: tx.date, transactionId: tx.id })
  }

  // R14
  const f14 = checkKhwPriceDrift(tx.salesRands, tx.qtySold, tx.productCode)
  if (f14) flags.push({ ...f14, date: tx.date, transactionId: tx.id })

  return flags
}

// Generate daily-level flags
export function generateDailyFlags(date, morningLogs, surplusEntries, salesByCategory, totalDailySales) {
  const flags = []

  // R13: Low revenue
  const f13 = checkLowRevenue(totalDailySales)
  if (f13) flags.push({ ...f13, date })

  for (const cat of Object.keys(salesByCategory)) {
    const opening = morningLogs.find(m => m.category === cat)?.value || 0
    const surplus = surplusEntries.find(s => s.category === cat)?.value || 0
    const actual = salesByCategory[cat] || 0

    // R7-R8: Waste
    const fWaste = checkWaste(opening, surplus, cat)
    if (fWaste) flags.push({ ...fWaste, date })

    // R10: Restock not logged
    const fRestock = checkRestockNotLogged(opening, actual, cat)
    if (fRestock) flags.push({ ...fRestock, date })

    // R11: Orphaned surplus
    const fOrphan = checkOrphanedSurplus(opening, surplus, cat)
    if (fOrphan) flags.push({ ...fOrphan, date })

    // R9: Underperformance
    const catConfig = getCategoryConfig(cat)
    if (catConfig?.projectable && cat !== 'pap only') {
      let projected = 0
      if (catConfig.type === 'fixed_unit') {
        projected = (opening - surplus) * catConfig.unitPrice
      } else if (catConfig.type === 'continuous_variable' || catConfig.type === 'discrete') {
        projected = opening - surplus
      }
      const fPerf = checkUnderperformance(projected, actual, cat)
      if (fPerf) flags.push({ ...fPerf, date })
    }
  }

  return flags
}

export function getSeverityColor(severity) {
  switch (severity) {
    case 'HIGH': return 'text-red-400 bg-red-950 border-red-800'
    case 'MEDIUM': return 'text-amber-400 bg-amber-950 border-amber-800'
    case 'LOW': return 'text-blue-400 bg-blue-950 border-blue-800'
    default: return 'text-gray-400 bg-gray-800 border-gray-700'
  }
}

export function getSeverityBadge(severity) {
  switch (severity) {
    case 'HIGH': return 'bg-red-600 text-white'
    case 'MEDIUM': return 'bg-amber-600 text-white'
    case 'LOW': return 'bg-blue-600 text-white'
    default: return 'bg-gray-600 text-white'
  }
}
